import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Building2, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { DateCell } from "@/components/DateCell";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface RecurringTemplate {
  id: string;
  templateName: string;
  status: string;
  frequency: string;
  daysOfWeek: string[];
  generationLeadDays: number;
  nextGenerationDate: string | null;
  createdAt: string;
  account?: { id: string; name: string };
}

interface TemplatesResponse {
  templates: RecurringTemplate[];
  total: number;
}

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "ACTIVE", label: "Active" },
  { value: "PAUSED", label: "Paused" },
  { value: "CANCELLED", label: "Cancelled" },
];

export default function RecurringTemplatesPage() {
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const { data, isLoading } = useQuery<TemplatesResponse>({
    queryKey: ["/api/recurring-templates", { search: searchValue, page: currentPage, limit: pageSize, status: statusFilter }],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(currentPage),
        limit: String(pageSize),
      });
      if (searchValue) params.set("search", searchValue);
      if (statusFilter !== "all") params.set("status", statusFilter);
      return api.get<TemplatesResponse>(`/recurring-templates?${params}`, sessionId);
    },
  });

  const columns = [
    {
      key: "createdAt",
      header: "Entry",
      sortable: true,
      cell: (row: RecurringTemplate) => <DateCell value={row.createdAt} className="text-muted-foreground" />,
    },
    {
      key: "templateName",
      header: "Template Name",
      sortable: true,
      cell: (row: RecurringTemplate) => (
        <div className="font-medium" data-testid={`text-template-name-${row.id}`}>{row.templateName}</div>
      ),
    },
    {
      key: "account",
      header: "Account",
      cell: (row: RecurringTemplate) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span>{row.account?.name || "-"}</span>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row: RecurringTemplate) => <StatusBadge status={row.status} type="order" />,
    },
    {
      key: "frequency",
      header: "Frequency",
      cell: (row: RecurringTemplate) => <Badge variant="outline">{row.frequency}</Badge>,
    },
    {
      key: "daysOfWeek",
      header: "Days",
      cell: (row: RecurringTemplate) => (
        <span className="text-xs text-muted-foreground">{row.daysOfWeek?.join(", ") || "-"}</span>
      ),
    },
    {
      key: "nextGenerationDate",
      header: "Next Generation",
      cell: (row: RecurringTemplate) => <DateCell value={row.nextGenerationDate} className="text-muted-foreground" fallback="-" />,
    },
    {
      key: "generationLeadDays",
      header: "Lead Days",
      className: "text-right",
      cell: (row: RecurringTemplate) => <span className="font-medium">{row.generationLeadDays}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Recurring Order Templates"
        description="Standing shopping lists for automated order generation"
        actions={
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search templates..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="w-64"
            />
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={data?.templates || []}
        totalCount={data?.total || 0}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        isLoading={isLoading}
        onRowClick={(row) => setLocation(`/recurring-templates/${row.id}`)}
        emptyIcon={<RefreshCw className="h-12 w-12 text-muted-foreground/50" />}
        emptyMessage="No recurring templates"
      />
    </div>
  );
}
